import type { AxiosProgressEvent, AxiosPromise } from "axios";
import Api from "api/Api";
import type { ApiResponse } from "api/ApiResponses";
import type { WorkspaceRole, Workspace } from "ee/constants/workspaceConstants";

export interface FetchDrawDBAppsResponse extends ApiResponse {
  data: any[];
}
export interface FetchDrawDBAppResponse extends ApiResponse {
  data: any;
}

export interface FetchDrawDBRequest {
  drawdbGroupId: string;
  name?: string;
}

export interface FetchDrawDBAppRequest {
  id: string;
}

export interface ChangeUserRoleRequest {
  workspaceId: string;
  newPermissionGroupId?: string;
  username: string;
}

export interface DeleteWorkspaceUserRequest {
  workspaceId: string;
  username: string;
}

export interface SaveDrawDBRequest {
  id: string;
  name?: string;
  website?: string;
  email?: string;
}

export interface SaveWorkspaceLogo {
  id: string;
  logo: File;
  progress: (progressEvent: AxiosProgressEvent) => void;
}

export interface CreateDrawDBRequest {
  name: string;
  dbType?: string;
  drawdbGroupId?: string;
  layout?: any;
}

export interface UpdateDrawDBRequest {
  id: string;
  name?: string;
  dbType?: string;
  layout?: any;
}

class DrawDBApi extends Api {
  static drawDBAppURL = "v1/drawdbApp";
  static drawDBGroupURL = "v1/drawdbGroups";
  static async fetchAllDrawDBGroups(): Promise<
    AxiosPromise<FetchDrawDBAppsResponse>
  > {
    return Api.get(`${DrawDBApi.drawDBGroupURL}/home`);
  }

  static async fetchDrawDBApps(
    request: FetchDrawDBRequest,
  ): Promise<AxiosPromise<FetchDrawDBAppsResponse>> {
    return Api.get(
      `${DrawDBApi.drawDBAppURL}/home?drawdbGroupId=${request.drawdbGroupId}&name=${request.name}`,
    );
  }

  static async createDrawDBGroup(
    request: CreateDrawDBRequest,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.post(DrawDBApi.drawDBGroupURL, request);
  }

  static async updateDrawDBGroup(
    request: UpdateDrawDBRequest,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.put(DrawDBApi.drawDBGroupURL + "/" + request.id, request);
  }

  static async createDrawDB(
    request: CreateDrawDBRequest,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.post(DrawDBApi.drawDBAppURL, request);
  }

  static async fetchDrawDB(
    request: FetchDrawDBRequest,
  ): Promise<AxiosPromise<FetchDrawDBAppResponse>> {
    return Api.get(DrawDBApi.drawDBAppURL + "/" + request.drawdbGroupId);
  }

  static async fetchDrawDBApp(
    request: FetchDrawDBAppRequest,
  ): Promise<AxiosPromise<FetchDrawDBAppResponse>> {
    return Api.get(DrawDBApi.drawDBAppURL + "/" + request.id);
  }

  static async editDrawDB(request: any): Promise<AxiosPromise<ApiResponse>> {
    return Api.put(DrawDBApi.drawDBAppURL + "/" + request.id, request);
  }

  static async deleteDrawDBApp(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.delete(DrawDBApi.drawDBAppURL + "/" + request.id);
  }

  static async deleteDrawDBGroup(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.delete(DrawDBApi.drawDBGroupURL + "/" + request.id);
  }

  // 导出
  static async exportDrawDBApp(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.get(DrawDBApi.drawDBAppURL + "/export/" + request.id, null, {
      responseType: "blob",
    });
  }

  // 导入
  static async importDrawDBApp(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    const formData = new FormData();
    formData.append("file", request.file);
    return Api.post(
      DrawDBApi.drawDBAppURL + "/import?drawdbGroupId=" + request.id,
      formData,
      null,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
  }

  static async inviteUsersToDrawDBGroup(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.post(DrawDBApi.drawDBGroupURL + "/invite", request);
  }

  static async fetchDrawDBGroupUsers(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.get(DrawDBApi.drawDBGroupURL + `/${request.id}/members`);
  }

  static async deleteDrawDBGroupMember(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.put(DrawDBApi.drawDBGroupURL + "/removeMember", request);
  }

  static async fetchDrawDBPermissionGroups(
    request: any,
  ): Promise<AxiosPromise<ApiResponse>> {
    return Api.get(DrawDBApi.drawDBGroupURL + `/${request.id}/permissionGroups`);
  }
  
}
export default DrawDBApi;
